import moment from "moment";
import { useAuth } from '@erpsquad/common/runtime';
import _ from "lodash";
// Note: These action creators should be provided by the consuming application
// Creating stubs for build compatibility
const createStub = (name: string) => () => {
  console.warn(`${name} is not implemented. This function should be provided by the consuming application.`);
  return Promise.resolve([]);
};

const getAgreement = createStub('getAgreement');
const getBins = createStub('getBins');
const getCategoryItems = createStub('getCategoryItems');
const getChartOfAccounts = createStub('getChartOfAccounts');
const getCompanies = createStub('getCompanies');
const getCurrency = createStub('getCurrency');
const getCustomerSegments = createStub('getCustomerSegments');
const getDepartments = createStub('getDepartments');
const getDiscountedItems = createStub('getDiscountedItems');
const getIncoTerms = createStub('getIncoTerms');
const getItems = createStub('getItems');
const getLocation = createStub('getLocation');
const getParties = createStub('getParties');
const getPartiesIndustry = createStub('getPartiesIndustry');
const getPaymentTerm = createStub('getPaymentTerm');
const getPurchaseInvoices = createStub('getPurchaseInvoices');
const getPurchaseOrderReceipts = createStub('getPurchaseOrderReceipts');
const getPurchaseRepresentatives = createStub('getPurchaseRepresentatives');
const getSalesPerason = createStub('getSalesPerason');
const getShippingRules = createStub('getShippingRules');
const getTaxTemplate = createStub('getTaxTemplate');
const getUoms = createStub('getUoms');
import { getCountries } from "libphonenumber-js";
export const formatNumber = (
  num: number,
  defaultCurrencySymbol: string = "$"
): string => {
  if (num >= 1_000_000) {
    return `${defaultCurrencySymbol} ${(num / 1_000_000).toFixed(1)} M`;
  } else if (num >= 100_000) {
    return `${defaultCurrencySymbol} ${(num / 100_000).toFixed(1)} L`; // L for Lakhs
  } else if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)} K`;
  } else {
    return `${defaultCurrencySymbol} ${num.toFixed(2)}`;
  }
};

export function formateDate(date, format = "DD-MM-YYYY") {
  if (!date) {
    return;
  }
  return moment(date).format(format);
}

export function capitalizeWords(str) {
  return str
    ? str
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ")
    : "";
}

export const defaultUserCurrency = () => {
  // eslint-disable-next-line
  const { user } = useAuth();
  return user?.currency_data?.symbol || "";
};
export const valueWithCurrency = (value: any) => {
  // eslint-disable-next-line
  const { user } = useAuth();
  if (!value) {
    return;
  }
  return `${user?.currency_data?.symbol || ""} ${value}`;
};

export const sortData = (data: any[], key: string, direction: string) => {
  const formatedData = _.orderBy(data, key, direction);
  return formatedData;
};
export function recursiveSort(
  data: any[],
  nestedKeys: any[],
  header: string,
  direction: string
) {
  if (!nestedKeys.length) return sortData(data, header, direction);
  const [currentKey, ...remainingKey] = nestedKeys;
  return data?.map((item) => {
    return {
      ...item,
      [currentKey]: recursiveSort(
        item[currentKey],
        remainingKey || [],
        header,
        direction
      ),
    };
  });
}

export function formatTime(isoString: string, format: string = "HH:mm:ss") {
  if (!isoString) {
    return;
  }
  const date = new Date(isoString); // Parse the ISO string

  const padZero = (num: number) => (num < 10 ? "0" : "") + num;

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  if (format === "HH:mm:ss") {
    return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
  }

  if (format === "HH:mm") {
    return `${padZero(hours)}:${padZero(minutes)}`;
  }

  if (format === "hh:mm A") {
    const period = hours >= 12 ? "PM" : "AM";
    const twelveHour = hours % 12 || 12;
    return `${padZero(twelveHour)}:${padZero(minutes)} ${period}`;
  }

  return date.toTimeString().slice(0, 8); // Default: HH:mm:ss
}

type Nullable<T> = {
  [P in keyof T]: T[P] extends object ? Nullable<T[P]> : T[P] | null;
};

export function convertEmptyToNull<T extends Record<string, any>>(
  obj: T
): Nullable<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (
      value === "" ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) && // Prevent arrays from being treated as objects
        Object.keys(value).length === 0)
    ) {
      acc[key as keyof T] = null;
    } else if (Array.isArray(value)) {
      acc[key as keyof T] = value.map((item) =>
        typeof item === "object" && item !== null
          ? convertEmptyToNull(item) // Recursively handle array elements
          : item
      );
    } else if (typeof value === "object" && value !== null) {
      acc[key as keyof T] = convertEmptyToNull(value);
    } else {
      acc[key as keyof T] = value;
    }
    return acc;
  }, {} as Nullable<T>);
}

function checkPattern(i: any, pattern: any[]) {
  return pattern.some((obj) => i.includes(obj));
}

export function removeExtraKeys(data: any, extraKeys = []) {
  const pattern = [
    "_at",
    "_by",
    "_data",
    "created_user",
    "updated_user",
    ...extraKeys,
  ];
  for (const i in data) {
    if (checkPattern(i, pattern)) {
      delete data[i];
    } else if (typeof data[i] === "object" && !Array.isArray(data[i])) {
      removeExtraKeys(data[i], ["_at", "_by", "_data"]);
    }
  }

  return data;
}

export const apiType = {
  company: getCompanies,
  items: getItems,
  currency: getCurrency,
  salesperson: getSalesPerason,
  party: getParties,
  department: getDepartments,
  location: getLocation,
  uom: getUoms,
  discountedItems: getDiscountedItems,
  taxTemplate: getTaxTemplate,
  paymentTerm: getPaymentTerm,
  incoTerms: getIncoTerms,
  shippingRules:getShippingRules,
  bins:getBins,
  agreement: getAgreement,
  accounts: getChartOfAccounts,
  invoices: getPurchaseInvoices,
  purchaseOrderReceipts: getPurchaseOrderReceipts,
  representatives: getPurchaseRepresentatives,
  industry:getPartiesIndustry,
  categoryItems:getCategoryItems,
  customerSegment:getCustomerSegments,
  countries:getCountries,
};
